<script lang="ts" setup>
import { Icon } from "@/components/Ui";
import { computed } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import { landingCards, type TCard, type TLandingCard } from "@/config/cards";
import { isMobile } from "~/helpers/breakpoints";

interface Props {
  cards: TCard[];
  active?: string;
  isDown?: boolean;
}

const props = defineProps<Props>();
const localePath = useLocalePath();
const template = computed<number>(() => {
  return props.cards.length + 1;
});
const showDropdown = ref<boolean>(false);
const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};
const closeDropdown = () => {
  if (showDropdown.value) toggleDropdown();
  return;
};
const moreCards = computed<TLandingCard[]>(() => {
  return landingCards.filter(
    (card: TLandingCard) =>
      !["google", "facebook", "tiktok"].includes(card.slug),
  );
});
</script>

<template>
  <div>
    <div class="container">
      <div :class="`root template-${template} down-${props.isDown}`">
        <nuxt-link
          v-for="(card, index) in props.cards"
          :key="index"
          :to="localePath(`${card.learn_more_link}`)"
          class="group"
        >
          <Icon
            :name="`pst-${card.type}-small`"
            path="cards"
          />
          <span
            :class="{
              'bg-neutral-100 group-hover:bg-transparent':
                card.type === props.active,
            }"
          >
            {{ card.title }}
          </span>
        </nuxt-link>

        <!--        More cards-->
        <div
          v-if="!isMobile"
          class="more group relative w-fit"
          @click="toggleDropdown"
        >
          <div class="hidden md:block relative w-fit">
            <Icon
              :class="{ 'dropdown-active': showDropdown }"
              :name="`pst-default-small`"
              class="w-[55px] h-9 group-hover:ring-2 group-hover:ring-neutral-200 rounded-[4px] transition-all"
              path="cards"
            />
            <span class="absolute top-0.5 right-0.5">
              <Icon
                :class="{ 'rotate-180': showDropdown }"
                class="h-4 w-4 transition-all"
                name="chevron-down"
              />
            </span>
            <transition name="fade-slide-up">
              <div
                v-show="showDropdown"
                v-on-click-outside="closeDropdown"
                class="dropdown"
              >
                <ul class="flex flex-col text-[14px] leading-[16px] p-[6px]">
                  <li
                    v-for="card in moreCards"
                    class="hover:bg-[#F3F4F7] cursor-pointer rounded-[4px]"
                  >
                    <nuxt-link
                      :to="localePath(`/cards/${card.slug}`)"
                      class="block w-full p-2"
                    >
                      {{ card.title }}
                    </nuxt-link>
                  </li>
                </ul>
              </div>
            </transition>
          </div>
          <span> {{ $t("cards.more-cards") }} </span>
        </div>
        <template v-else>
          <UiButton
            class="more-button col-span-3"
            color="custom"
            icon-pos="right"
            size="custom"
            @click="toggleDropdown"
          >
            {{ $t("cards.more-cards") }}
            <Icon
              :class="{ 'rotate-180': showDropdown }"
              class="h-4 w-4 transition-all"
              name="chevron-down"
            />
          </UiButton>
          <Teleport to="body">
            <transition name="slide-up">
              <div
                v-if="isMobile && showDropdown"
                class="fixed top-0 left-0 right-0 bottom-0 bg-white w-full h-full z-[2000]"
              >
                <div
                  class="overflow-y-auto h-full flex flex-col gap-3 p-5 absolute w-full"
                >
                  <span
                    class="close absolute top-0 right-0 m-5"
                    @click="closeDropdown"
                  >
                    <Icon name="close-outline" />
                  </span>

                  <ul class="flex flex-col text-lg">
                    <li
                      v-for="card in moreCards"
                      class="hover:bg-[#F3F4F7] cursor-pointer rounded-[4px]"
                    >
                      <nuxt-link
                        :to="localePath(`/cards/${card.slug}`)"
                        class="block w-full py-3"
                      >
                        {{ card.title }}
                      </nuxt-link>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </Teleport>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.root {
  @apply w-full grid gap-3 py-2 border-t border-neutral-200;
  @apply lg:gap-y-0 xl:px-16;
  & > a {
    &:hover,
    &:focus,
    &:active {
      @apply bg-neutral-100 rounded-[12px];
    }
  }

  & > a {
    @apply w-full text-sm flex flex-col items-center gap-2 pt-2 text-center transition-all cursor-pointer leading-none;
    @apply lg:px-2;

    & span {
      @apply py-2 px-[6px] rounded-[4px] transition-all;
    }
  }

  & > a {
    & svg {
      @apply w-[55px] h-9;
    }
  }
}

.template {
  &-5 {
    @apply grid-cols-6 md:grid-cols-5;
    & > a {
      &:nth-child(-n + 3) {
        @apply col-span-2;
        @apply md:col-span-1;
      }

      &:nth-child(n + 4) {
        @apply col-span-3;
        @apply md:col-span-1;
      }
    }
  }

  &-6 {
    @apply grid-cols-3 sm:grid-cols-6;
  }

  &-7 {
    @apply grid-cols-3 sm:grid-cols-7;
  }
}

.down {
  &-true {
    @apply border-transparent;
  }
}

.more {
  @apply w-full text-sm hidden md:flex flex-col items-center gap-2 pt-2 text-center transition-all cursor-pointer leading-none;
  @apply lg:px-2;

  & span {
    @apply py-2 px-[6px] rounded-[4px] transition-all;
  }
}

.dropdown {
  @apply transition-all block absolute -right-0.5 top-10 bottom-auto text-left rounded-[6px] bg-white border border-[#D0D4DD] w-[200px];
  box-shadow: 0px 10px 16px -3px #14151a14;

  &-active {
    @apply ring-2 ring-neutral-200;
  }
}

.more-button {
  @apply bg-[#D6D6D6] h-9;
}

.close {
  @apply ltr:ml-auto rtl:mr-auto flex items-center justify-center h-12 w-12 rounded-full bg-black/20 text-white;
  @apply transition-all focus:bg-black focus:bg-opacity-30 focus:outline-none;
  @apply cursor-pointer transition-all;
  @apply hover:scale-[0.9];
  @apply dark:bg-white dark:bg-opacity-20;

  & svg {
    @apply w-6 h-6 min-w-[24px];
  }
}
</style>
